import { useState, useCallback } from 'react';
import { useDebounce } from './useDebounce';
import { TableStateConfig, SortOrder } from '@/types/table';

export function useTableState(config: TableStateConfig = {}) {
    const [page, setPage] = useState(config.initialPage ?? 1);
    const [pageSize] = useState(config.initialPageSize ?? 20);
    const [search, setSearch] = useState(config.initialSearch ?? '');
    const [sortBy, setSortBy] = useState<string | undefined>(
        config.initialSort?.by,
    );
    const [sortOrder, setSortOrder] = useState<SortOrder>(
        config.initialSort?.order ?? SortOrder.ASC,
    );

    const debouncedSearch = useDebounce(search, config.debounceMs ?? 300);

    const handleSortChange = useCallback(
        (newSortBy: string, newSortOrder: SortOrder) => {
            setSortBy(newSortBy);
            setSortOrder(newSortOrder);
            setPage(1);
        },
        [],
    );

    const handleSearchChange = useCallback((newSearch: string) => {
        setSearch(newSearch);
        setPage(1);
    }, []);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const reset = useCallback(() => {
        setPage(1);
        setSearch('');
        setSortBy(undefined);
        setSortOrder(SortOrder.ASC);
    }, []);

    return {
        page,
        pageSize,
        search,
        debouncedSearch,
        sortBy,
        sortOrder,
        onPageChange: handlePageChange,
        onSearchChange: handleSearchChange,
        onSortChange: handleSortChange,
        reset,
    };
}
